import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { logger } from '../utils/Logger';
import Config from '../chat/models/Config';
import PushHandler from '../chat/network/PushHandler';
import { getCookie } from '../network/cookie';
import { startRing, stopRing } from './ring_audio';

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyDi8BSg5aVy95EMFONvYNmhU7dIKowadbE',
  authDomain: 'myt-student-mobile.firebaseapp.com',
  databaseURL: 'https://myt-student-mobile.firebaseio.com',
  projectId: 'myt-student-mobile',
  storageBucket: 'myt-student-mobile.appspot.com',
  messagingSenderId: '401802094788',
  appId: '1:401802094788:web:56699853c1fd1aae'
};

interface ActiveRing {
  notification: Notification | null;
  timeoutId: ReturnType<typeof setTimeout> | null;
  repostId: ReturnType<typeof setInterval> | null;
}

// Must outlast the server's dial window, or the agent stops being told about a
// call that is still answerable. The backend rings the agent's browser for
// `Timeout: "25"` seconds — see incomingDialTwiML in goserver
// app/external/twilio_call/api_dispatch.go — so anything below 25s cuts the
// ring off early. It used to be 15s, which silently killed the ring (and the
// OS banner) ~10s before Twilio gave up, and inbound calls were missed while
// still ringing. Keep this ABOVE the backend timeout; if that timeout changes,
// change this with it.
const INCOMING_RING_MAX_MS = 30000;

// Chrome ignores `requireInteraction` for web notifications, so the OS banner
// auto-dismisses after a few seconds while the call keeps ringing. Agents who
// are on another CMS page and watching the banner (rather than the in-app pill)
// see the call "disappear" almost immediately. Re-posting on the same `tag`
// replaces the existing banner instead of stacking, so the notification keeps
// surfacing for the whole ring window.
const INCOMING_RING_REPOST_MS = 6500;

export default class MytNotification {
  private static askedPushPermission = false;
  private static serviceWorkerRegistered = false;
  private static soundCooldownUntil = 0;
  private static activeRings = new Map<string, ActiveRing>();

  static getPermissionState = (): NotificationPermission | 'unsupported' => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unsupported';
    }

    return Notification.permission;
  };

  private static fetchToken = async () => {
    if (window.location.hostname.includes('local')) {
      logger.debug('MytNotification: skipping token fetch on local hostname.');
      return;
    }

    if (MytNotification.serviceWorkerRegistered) {
      logger.debug('MytNotification: service worker already registered.');
      return;
    }

    // Set early to avoid races from concurrent webPushInit calls.
    MytNotification.serviceWorkerRegistered = true;

    const registerPath = '/static/js/service_worker.js';

    try {
      logger.debug('MytNotification: registering service worker for push.');
      const serviceWorkerRegistration = await navigator.serviceWorker.register(registerPath);
      logger.debug('MytNotification: service worker registered successfully.');

      const app = initializeApp(FIREBASE_CONFIG);
      const messaging = getMessaging(app);

      onMessage(messaging, (payload) => {
        logger.debug('MytNotification received. ', payload);
      });

      const currentToken = await getToken(messaging, {
        serviceWorkerRegistration: serviceWorkerRegistration
      });

      if (!!currentToken) {
        logger.debug('MytNotification: push token fetched successfully.');
        Config.shared.updatePushToken(currentToken);
      } else {
        logger.debug('MytNotification: push token unavailable.');
      }

      if (Config.shared.cached().syncFcmPushToken) {
        PushHandler.shared.registerPush(currentToken);
      }
    } catch (err) {
      MytNotification.serviceWorkerRegistered = false;
      logger.debug('MytNotification: An error occurred while retrieving token. ', err);
    }
  };

  static ensurePermission = async ({
    userGesture = false
  }: {
    userGesture?: boolean;
  } = {}): Promise<NotificationPermission | 'unsupported'> => {
    const permissionState = MytNotification.getPermissionState();

    logger.debug('MytNotification: ensurePermission called.', {
      permissionState,
      userGesture
    });

    if (permissionState === 'unsupported' || permissionState === 'denied') {
      return permissionState;
    }

    if (permissionState === 'granted') {
      await this.fetchToken();
      return permissionState;
    }

    if (!userGesture) {
      logger.debug('MytNotification: permission prompt skipped without user gesture.');
      return permissionState;
    }

    if (MytNotification.askedPushPermission) {
      logger.debug('MytNotification: permission prompt already requested in this session.');
      return MytNotification.getPermissionState();
    }

    MytNotification.askedPushPermission = true;

    const permission = await Notification.requestPermission();
    logger.debug('MytNotification: permission request resolved.', { permission });

    if (permission === 'granted') {
      await this.fetchToken();
    }

    return permission;
  };

  static webPushInit = () => {
    if (getCookie('is_impersonated') === 'true' || getCookie('impersonationFromTeacherToken')) {
      // no push token for impersonated student
      logger.debug('MytNotification: webPushInit skipped for impersonated user.');
      return;
    }

    const permissionState = MytNotification.getPermissionState();

    if (permissionState === 'unsupported') {
      logger.debug('MytNotification: browser notifications unsupported.');
      return 'This browser does not support desktop notification';
    }

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      if (MytNotification.serviceWorkerRegistered) {
        logger.debug('MytNotification: webPushInit skipped, already registered.');
        return;
      }

      isSupported().then((supported) => {
        logger.debug('MytNotification: firebase messaging support checked.', {
          supported,
          permissionState
        });

        if (supported) {
          if (permissionState === 'granted') {
            this.fetchToken();
          } else {
            logger.debug('MytNotification: push init waiting for granted permission.');
          }
        }
      });
    } else {
      logger.debug('MytNotification: push APIs unavailable in current browser.');
    }
  };

  static canShowLocalNotification = (): boolean => {
    const permissionState = MytNotification.getPermissionState();
    const canShow = permissionState === 'granted';

    if (!canShow) {
      logger.debug('MytNotification: local notification not allowed.', {
        permissionState
      });
    }

    return canShow;
  };

  static showLocalNotification = (
    notificationTag: string,
    notificationBody: string = 'You got a new message',
    onClick?: (notificationTag: string) => void,
    // Optional custom title. Defaults to the brand string so existing
    // callers (twilio incoming/missed calls, chat) keep their behaviour.
    notificationTitleOverride?: string
  ): boolean => {
    if (!MytNotification.canShowLocalNotification()) {
      return false;
    }

    const notificationTitle = notificationTitleOverride || 'MyYogaTeacher';

    logger.debug('MytNotification: showing local notification.', {
      notificationTag
    });

    this.playNotificationSound();

    const notification = new Notification(notificationTitle, {
      body: notificationBody,
      icon: '/static/images/notification_icon_256.png',
      badge: '/static/images/notification_icon_128.png',
      silent: false,
      tag: notificationTag
    });

    notification.onclick = () => {
      if (!!window) {
        window.focus();
      }

      onClick?.(notificationTag);
      notification.close();
    };

    return true;
  };

  static startIncomingCallRing = (
    ringId: string,
    notificationBody: string,
    onClick?: (ringId: string) => void,
    // A warm-transfer ring lasts longer than a routed call's dial window (PROD-10258).
    maxRingMs: number = INCOMING_RING_MAX_MS
  ): void => {
    if (typeof window === 'undefined') {
      return;
    }

    // Already ringing — no-op.
    if (MytNotification.activeRings.has(ringId)) {
      return;
    }

    // One shared element for every ring, so it stays unblocked on Safari — see
    // ring_audio.ts. Stopped only once the last concurrent ring is gone.
    startRing();

    let notification: Notification | null = null;
    const permission = MytNotification.getPermissionState();

    if (permission !== 'granted') {
      // Ring still plays, but an OS banner is impossible without permission.
      // Surface this prominently so the agent knows to unblock.
      // eslint-disable-next-line no-console
      console.warn(
        '[incoming-call] Ring is playing but OS notification was skipped — Notification.permission =',
        permission,
        '. Grant notifications for this origin (lock icon → Notifications) and ensure macOS System Settings → Notifications → Google Chrome is enabled.'
      );
    } else {
      try {
        notification = new Notification('Incoming call', {
          body: notificationBody,
          icon: '/static/images/notification_icon_256.png',
          badge: '/static/images/notification_icon_128.png',
          silent: true,
          tag: ringId,
          requireInteraction: true
        });

        notification.onclick = () => {
          if (!!window) {
            window.focus();
          }
          onClick?.(ringId);
          notification?.close();
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[incoming-call] Notification constructor threw', error);
        logger.debug('MytNotification: incoming call notification init failed.', error);
      }
    }

    const timeoutId = setTimeout(() => {
      MytNotification.stopIncomingCallRing(ringId);
    }, maxRingMs);

    // Keep the banner on screen for the full ring window (see
    // INCOMING_RING_REPOST_MS). Only meaningful when we were able to post one
    // in the first place; without permission there is nothing to refresh.
    let repostId: ReturnType<typeof setInterval> | null = null;
    if (permission === 'granted') {
      repostId = setInterval(() => {
        const entry = MytNotification.activeRings.get(ringId);
        if (!entry) {
          return;
        }
        try {
          entry.notification?.close();
          entry.notification = new Notification('Incoming call', {
            body: notificationBody,
            icon: '/static/images/notification_icon_256.png',
            badge: '/static/images/notification_icon_128.png',
            silent: true,
            tag: ringId,
            requireInteraction: true
          });
          entry.notification.onclick = () => {
            if (!!window) {
              window.focus();
            }
            onClick?.(ringId);
            entry.notification?.close();
          };
        } catch (error) {
          logger.debug('MytNotification: incoming call notification repost failed.', error);
        }
      }, INCOMING_RING_REPOST_MS);
    }

    MytNotification.activeRings.set(ringId, {
      notification,
      timeoutId,
      repostId
    });
  };

  static stopIncomingCallRing = (ringId: string): void => {
    const entry = MytNotification.activeRings.get(ringId);
    if (!entry) {
      return;
    }

    if (entry.timeoutId) {
      clearTimeout(entry.timeoutId);
    }

    if (entry.repostId) {
      clearInterval(entry.repostId);
    }

    try {
      entry.notification?.close();
    } catch (error) {
      logger.debug('MytNotification: failed to close ring notification.', error);
    }

    MytNotification.activeRings.delete(ringId);

    // Shared element: silence it only when no other call is still ringing.
    if (MytNotification.activeRings.size === 0) {
      stopRing();
    }
  };

  private static playNotificationSound = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (document.visibilityState !== 'visible') {
      return;
    }

    const now = Date.now();
    if (now < MytNotification.soundCooldownUntil) {
      return;
    }

    MytNotification.soundCooldownUntil = now + 1500;

    try {
      const audio = new Audio('/static/sounds/notification.wav');
      audio.volume = 1;
      audio.play().catch((error) => {
        logger.debug('MytNotification: notification sound playback blocked.', error);
      });
    } catch (error) {
      logger.debug('MytNotification: notification sound init failed.', error);
    }
  };
}
